import { createSelector } from 'reselect';
import get from 'lodash/get';
import { useLocation } from 'react-router-dom';
import humanizeUtil from '@utils/humanizeUtil';
import { THRESHOLD_INVERTED_METRICS } from '@utils/metrics';
import { getPercentageMetricByKey, getMetricMetaByKey } from '@utils/metrics/metricHelpers';
import { initThresholdSelector } from '@store/noc/threshold/selectors';
import { findMatchedBaseLineMetric } from '@components/noc-metric-panel/utils/constants';
import { isAppModule } from '@src/utils/constants';

export const THRESHOLD_DEFAULT = 'Default';
export const THRESHOLD_GOOD = 'Good';
export const THRESHOLD_CRITICAL = 'Critical';
export const THRESHOLD_WARNING = 'Warning';

export const THRESHOLD_WARNING_VALUE = 0.9;

export const THRESHOLD_TYPE_CUSTOM = 'custom';
export const THRESHOLD_TYPE_CONVIVA = 'conviva';

// baseline values are common for both views
export const thresholdBaselineSelector = createSelector(
  initThresholdSelector,
  (res) => res.data.threshold_baseline || {},
);

// calculate threshold for a row
export const calcRowThreshold = ({ rowData, metrics, thresholds = {}, thresholdBaseline = {} }) => {
  const thresholdCategory = {};
  metrics.forEach((metric) => {
    thresholdCategory[metric] = calcThreshold({
      rowData,
      metric,
      threshold: thresholds[rowData.id] || {},
      thresholdBaseline,
    });
  });

  // calculate scope threshold
  const categories = Object.values(thresholdCategory).map((i) => i.category);
  const hasCriticalValue = categories.includes(THRESHOLD_CRITICAL);
  const hasWarningValue = categories.includes(THRESHOLD_WARNING);

  let rowCategory = THRESHOLD_GOOD;
  if (hasCriticalValue) {
    rowCategory = THRESHOLD_CRITICAL;
  }
  if (hasWarningValue) {
    rowCategory = THRESHOLD_WARNING;
  }

  thresholdCategory[rowData.id] = rowCategory;
  thresholdCategory.isCritical = hasCriticalValue ? 1 : 0;
  thresholdCategory.isWarning = hasWarningValue ? 1 : 0;
  // done
  return thresholdCategory;
};

// threshold type
const getThresholdType = (record, metric) =>
  get(record, [metric, 'isCustomSelected'], false) ? THRESHOLD_TYPE_CUSTOM : THRESHOLD_TYPE_CONVIVA;

const isThresholdWarning = (metricValue, metricThreshold) => {
  if (metricThreshold) {
    return Math.abs(metricValue - metricThreshold) / metricThreshold <= 1 - THRESHOLD_WARNING_VALUE;
  }
  return false;
};

const highIsGoodMetric = (metric) => {
  // fix issue: https://conviva.atlassian.net/browse/HP-9428
  // keep higher value is better logic same with trends
  if (isAppModule) {
    const targetMetricMeta = getMetricMetaByKey({ metricKey: metric });
    return !targetMetricMeta?.reverse;
  }
  // TODO: for now video/ads higher value is better metric are hardcoded
  // Actually it could read reverse property rather hardcoded list
  // Need to double check with PM. For now just fix the issue on DPI.
  const baseMetrics = [...THRESHOLD_INVERTED_METRICS];
  const percentageMetrcis = baseMetrics.map((it) =>
    getPercentageMetricByKey({
      metricKey: it,
      defaultValue: it,
    }),
  );
  const goodForHigh = baseMetrics.includes(metric) || percentageMetrcis.includes(metric);
  return goodForHigh;
};

// threshold state
const getThresholdCategory = (metric, metricValue, metricThreshold) => {
  const critical = highIsGoodMetric(metric)
    ? metricValue < metricThreshold
    : metricValue > metricThreshold;

  if (critical) {
    return THRESHOLD_CRITICAL;
  }

  const warning = isThresholdWarning(metricValue, metricThreshold);

  if (warning) {
    return THRESHOLD_WARNING;
  }

  return THRESHOLD_GOOD;
};

// returns threshold value for a cell
export const calcThreshold = ({ rowData, metric, threshold = {}, thresholdBaseline = {} }) => {
  let category = THRESHOLD_DEFAULT;
  let applyThreshold = true;

  const metricValue = rowData[metric];

  // check and apply thresholds
  const thresholdType = getThresholdType(threshold, metric);
  let metricThreshold = get(threshold, [metric, thresholdType], null);

  if (metricThreshold && metricThreshold > 0) {
    try {
      // check for baseline
      const baselineMetric = findMatchedBaseLineMetric(metric);

      if (baselineMetric) {
        // do not apply threshold if baseline is not available
        const baselineThresholdType = getThresholdType(thresholdBaseline, metric);
        const baseline = get(thresholdBaseline, [metric, baselineThresholdType], Infinity);
        // current baseline metric value
        const rowBaseline = rowData[baselineMetric];
        // apply threshold only when baseline is crossed
        if (rowBaseline < baseline) {
          applyThreshold = false;
        }
      }
    } catch (err) {
      // do not apply threshold in case of calc errors
      applyThreshold = false;
    }

    if (metric === 'Bitrate') {
      metricThreshold = metricThreshold * 1024;
    }

    // find treshold category
    if (applyThreshold) {
      category = getThresholdCategory(metric, metricValue, metricThreshold);
    }
  }
  return {
    category,
    metricThreshold,
  };
};

export const humanizeValue = (number: number, unit: string) => humanizeUtil(number, '', unit);

export const autoSort = (isAutoSortEnabled, data) => {
  // moving red tiles to the top
  if (isAutoSortEnabled) {
    data.sort((item1, item2) => {
      if (item1.threshold.isCritical === item2.threshold.isCritical) {
        return item2.threshold.isWarning - item1.threshold.isWarning;
      }
      return item2.threshold.isCritical - item1.threshold.isCritical;
    });
  }
};

export const CheckIfUpdateDashboardPopupIsOpen = (updatePopupObj = {}) => {
  const location = useLocation();
  const currentLocation = location.pathname;
  const params = currentLocation.split('/');
  const activeDashboardId = params[params.length - 1];
  return updatePopupObj[activeDashboardId] && updatePopupObj[activeDashboardId] === true;
};

export enum Category {
  Good = 'Good',
  Warning = 'Warning',
  Critical = 'Critical',
  Default = 'Default',
}
